import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedSessionId = sessionStorage.getItem('sessionId');
      const storedConnectionInfo = sessionStorage.getItem('connectionInfo');

      if (storedSessionId) {
        try {
          // Verify session is still valid
          const response = await fetch('/api/auth/status', {
            headers: {
              'x-session-token': storedSessionId
            }
          });

          if (!response.ok) {
            throw new Error('Session validation failed');
          }

          const data = await response.json();

          if (data.connected) {
            setSessionId(storedSessionId);
            setConnectionInfo(storedConnectionInfo ? JSON.parse(storedConnectionInfo) : null);
            setIsConnected(true);
            // Check if table mapping exists
            const hasMapping = sessionStorage.getItem('tableMapping');
            setSetupComplete(!!hasMapping);
          } else {
            // Session expired or invalid, clear storage
            sessionStorage.clear();
            setIsConnected(false);
            setSessionId(null);
            setConnectionInfo(null);
          }
        } catch (error) {
          console.error('Session check failed:', error);
          // Clear all session data on error - backend might be down
          sessionStorage.clear();
          setIsConnected(false);
          setSessionId(null);
          setConnectionInfo(null);
        }
      }

      setLoading(false);
    };

    checkSession();
  }, []);

  const connect = (newSessionId, info) => {
    setSessionId(newSessionId);
    setConnectionInfo(info);
    setIsConnected(true);
    setSetupComplete(false); // Reset setup when connecting
  };

  const completeSetup = () => {
    setSetupComplete(true);
  };

  const disconnect = async () => {
    if (sessionId) {
      try {
        await fetch('/api/auth/disconnect', {
          method: 'POST',
          headers: {
            'x-session-token': sessionId
          }
        });
      } catch (error) {
        console.error('Disconnect error:', error);
      }
    }

    // Clear state and storage
    setSessionId(null);
    setConnectionInfo(null);
    setIsConnected(false);
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('connectionInfo');
    
    // Clear saved filters
    sessionStorage.removeItem('worklogSummaryFilters');
    sessionStorage.removeItem('engineeringWorklogsFilters');
  };

  const getSessionToken = () => sessionId;

  const value = {
    isConnected,
    sessionId,
    connectionInfo,
    setupComplete,
    loading,
    connect,
    disconnect,
    completeSetup,
    getSessionToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
